import axios from "axios";
import type { AppThunk } from "@Store/index";
import { updateNewsData, updateNewsReadyStatus } from "./newsPageReducer";
import { getMetaSuccess } from "@App/appReducer";
import config from "@Config/index";

interface GetNewsPageDataParams {
  slug: string;
  url?: string;
  staticContext?: any;
  isCategory?: boolean;
  isTag?: boolean;
  isSearch?: boolean;
  searchQuery?: string;
  isLatest?: boolean;
}

const getNewsPageData =
  ({
    slug,
    url,
    staticContext,
    isCategory = false,
    isTag = false,
    isSearch = false,
    searchQuery = "",
    isLatest = false,
  }: GetNewsPageDataParams): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(updateNewsReadyStatus("request"));

      let apiUrl: string;
      if (isLatest) {
        apiUrl = `${config.CMS_API_URL}/api/news-posts?limit=100&sort=-date&select[id]=true&select[name]=true&select[slug]=true&select[mainImage]=true&select[category]=true&select[readTime]=true&select[date]=true&select[updatedAt]=true&select[postType]=true&where[_status][equals]=published&where[isArchived][equals]=false`;
      } else if (isCategory) {
        apiUrl = `${config.CMS_API_URL}/api/news-posts?where[category.slug][equals]=${slug}&select[id]=true&select[name]=true&select[slug]=true&select[mainImage]=true&select[category]=true&select[readTime]=true&select[date]=true&select[updatedAt]=true&select[postType]=true&limit=0&where[_status][equals]=published&where[isArchived][equals]=false`;
      } else if (isTag) {
        apiUrl = `${config.CMS_API_URL}/api/news-posts?where[tags.slug][equals]=${slug}&select[id]=true&select[name]=true&select[slug]=true&select[mainImage]=true&select[category]=true&select[readTime]=true&select[date]=true&select[updatedAt]=true&select[tags]=true&select[postType]=true&limit=0&where[_status][equals]=published&where[isArchived][equals]=false`;
      } else if (isSearch) {
        apiUrl = `${config.CMS_API_URL}/api/news-posts?where[name][contains]=${encodeURIComponent(
          searchQuery,
        )}&select[id]=true&select[name]=true&select[slug]=true&select[mainImage]=true&select[category]=true&select[readTime]=true&select[date]=true&select[updatedAt]=true&select[postType]=true&limit=0&where[_status][equals]=published&where[isArchived][equals]=false`;
      } else {
        apiUrl = `${config.CMS_API_URL}/api/news-posts?where[slug][equals]=${slug}&where[_status][equals]=published&where[isArchived][equals]=false`;
      }

      const res = await axios.get(apiUrl);

      if (isCategory || isTag || isSearch || isLatest) {
        let newsData = res.data?.docs || [];
        const totalDocs = res.data?.totalDocs || 0;

        let categories: any[] = [];
        try {
          const categoriesResponse = await axios.get(
            `${config.CMS_API_URL}/api/news-categories?select[id]=true&select[name]=true&select[slug]=true&limit=0&where[_status][equals]=published`,
          );
          categories = categoriesResponse.data?.docs || [];
        } catch (error) {
          console.error("Error fetching news categories:", error);
        }

        if (!newsData || newsData.length === 0) {
          if (isSearch) {
            const meta: any = {};
            meta.title = searchQuery
              ? `No Results for "${searchQuery}" | Amber News`
              : "Search News | Amber";
            meta.description = searchQuery
              ? `No news articles found for "${searchQuery}". Try searching with different keywords.`
              : "Search for news articles on Amber.";
            meta.canonical_url_suffix = searchQuery
              ? `/news/search?query=${encodeURIComponent(searchQuery)}`
              : "/news/search";
            meta.keywords = [];
            meta.robots = "noindex,follow";

            dispatch(getMetaSuccess(meta));
            dispatch(
              updateNewsData({
                categoryData: [],
                categories,
                slug,
                canonical_url: url,
                isCategory,
                isTag,
                isSearch,
                searchQuery,
                totalDocs: 0,
                isLatest,
              }),
            );
            dispatch(updateNewsReadyStatus("success"));
            return;
          }

          dispatch(updateNewsReadyStatus("failure"));
          if (staticContext) {
            staticContext.statusCode = 404;
          }
          return;
        }

        if (isTag && newsData.length > 0) {
          newsData = newsData.map((post: any) => {
            const matchedTag = post.tags?.find((tag: any) => tag.slug === slug);
            return { ...post, matchedTag };
          });
        }

        const meta: any = {};
        meta.excludeAlternateLinks = true;

        if (isLatest) {
          meta.title = "Latest News & Updates | Amber";
          meta.description =
            "Discover the latest student housing news from Amber. Stay updated with our newest articles and insights.";
          meta.canonical_url_suffix = "/news/latest";
        } else if (isCategory) {
          const categoryName = newsData[0]?.category?.name || slug;
          meta.title = `${categoryName} News | Amber`;
          meta.description = `Explore news about ${categoryName}. Find the latest articles and updates.`;
          meta.canonical_url_suffix = `/news/category/${slug}`;
        } else if (isTag) {
          const tagName = newsData[0]?.matchedTag?.name || slug;
          meta.title = `${tagName} News | Amber`;
          meta.description = `Read news tagged with ${tagName}. Discover relevant articles and insights.`;
          meta.canonical_url_suffix = `/news/tag/${slug}`;
        } else if (isSearch) {
          meta.title = searchQuery ? `News on "${searchQuery}" | Amber` : "Search News | Amber";
          meta.description = searchQuery
            ? `Search results for "${searchQuery}". Find relevant news articles.`
            : "Search for news articles on Amber.";
          meta.canonical_url_suffix = searchQuery
            ? `/news/search?query=${encodeURIComponent(searchQuery)}`
            : "/news/search";
          meta.robots = "noindex,follow";
        }

        meta.keywords = [];

        dispatch(getMetaSuccess(meta));
        dispatch(
          updateNewsData({
            categoryData: newsData,
            categories,
            slug,
            canonical_url: url,
            isCategory,
            isTag,
            isSearch,
            searchQuery,
            totalDocs,
            isLatest,
          }),
        );
      } else {
        let newsData = res.data?.docs?.[0] || res.data;

        if (newsData?.docs && Array.isArray(newsData.docs)) {
          // eslint-disable-next-line prefer-destructuring
          newsData = newsData.docs[0];
        }

        if (!newsData || !newsData.id) {
          dispatch(updateNewsReadyStatus("failure"));
          if (staticContext) {
            staticContext.statusCode = 404;
          }
          return;
        }

        // Fetch sidebar featured news
        const sidebarFeaturedResponse = await axios.get(
          `${config.CMS_API_URL}/api/news-posts?where[featured][equals]=true&select[id]=true&select[name]=true&select[slug]=true&select[mainImage]=true&select[category]=true&select[readTime]=true&select[date]=true&select[updatedAt]=true&select[postType]=true&limit=3&sort=-date&where[_status][equals]=published`,
        );
        const sidebarFeaturedNews = sidebarFeaturedResponse.data?.docs || [];

        const meta: any = {};
        meta.title = newsData?.metaDescription ? newsData.name : `${newsData.name} | Amber News`;
        meta.description = newsData?.metaDescription || "";
        meta.keywords = newsData?.metaKeywords?.split(",") || [];
        meta.canonical_url_suffix = `/news/post/${newsData?.slug}`;
        meta.excludeAlternateLinks = true;
        meta.dateModified = newsData.updatedAt
          ? new Date(newsData.updatedAt).toLocaleDateString("en-US", {
              month: "short",
              day: "2-digit",
              year: "numeric",
            })
          : "";

        dispatch(getMetaSuccess(meta));
        dispatch(
          updateNewsData({
            data: newsData,
            sidebarFeaturedNews,
            slug,
            canonical_url: url,
            isCategory: false,
          }),
        );
      }

      dispatch(updateNewsReadyStatus("success"));
    } catch (error) {
      console.error("Error fetching news data:", error);
      dispatch(updateNewsReadyStatus("failure"));
      if (staticContext) {
        staticContext.statusCode = 500;
      }
    }
  };

export default getNewsPageData;
